/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import { useMemo } from 'react'

import { Layer, Source } from 'react-map-gl/mapbox'

import { GEOFENCE_ZORDER_COLORS } from '@/lib/map/GeofencesInView'

import { ringFromCoordinates } from '../geometry'
import type { ManagedGeofence } from '../types'

export const GEOFENCE_FILL_LAYER = 'managed-geofence-fill'

// A Mapbox colour expression on the feature's `zorder`, from the shared palette.
const ZORDER_COLOR = [
  'match',
  ['get', 'zorder'],
  ...Object.entries(GEOFENCE_ZORDER_COLORS).flatMap(([zorder, color]) => [Number(zorder), color]),
  GEOFENCE_ZORDER_COLORS[1],
] as unknown as mapboxgl.ExpressionSpecification

/** The geofences as GeoJSON, drawn in reverse layer order so a higher layer paints on top. */
export const geofenceFeatures = (geofences: ManagedGeofence[]): GeoJSON.FeatureCollection<GeoJSON.Polygon> => ({
  type: 'FeatureCollection',
  features: [...geofences]
    .filter((g) => g.coordinates.length >= 3)
    .sort((a, b) => a.zorder - b.zorder)
    .map((g) => ({
      type: 'Feature',
      id: g.id,
      geometry: { type: 'Polygon', coordinates: [ringFromCoordinates(g.coordinates)] },
      properties: { id: g.id, name: g.name, zorder: g.zorder, visible: g.visible },
    })),
})

interface GeofenceLayersProps {
  geofences: ManagedGeofence[]
  /** Outlined thicker: the selected and the hovered geofence. */
  highlightIds?: number[]
  /** For context only (the editor): fainter, and never clickable. */
  muted?: boolean
  idPrefix?: string
}

/**
 * Company geofences on a react-map-gl map, filled and outlined in their layer's colour. Hidden
 * geofences (`visible = false`) are drawn too, dashed and fainter, since the people managing them
 * need to see them.
 */
export const GeofenceLayers = ({
  geofences,
  highlightIds = [],
  muted = false,
  idPrefix = 'managed-geofence',
}: GeofenceLayersProps) => {
  const data = useMemo(() => geofenceFeatures(geofences), [geofences])
  const fillOpacity = muted ? 0.06 : 0.15
  const lineOpacity = muted ? 0.45 : 0.9

  return (
    <Source id={`${idPrefix}-source`} type="geojson" data={data}>
      <Layer
        id={muted ? `${idPrefix}-fill` : GEOFENCE_FILL_LAYER}
        type="fill"
        paint={{
          'fill-color': ZORDER_COLOR,
          'fill-opacity': ['case', ['get', 'visible'], fillOpacity, fillOpacity / 2],
        }}
      />
      <Layer
        id={`${idPrefix}-line`}
        type="line"
        filter={['==', ['get', 'visible'], true]}
        paint={{ 'line-color': ZORDER_COLOR, 'line-width': 2, 'line-opacity': lineOpacity }}
      />
      <Layer
        id={`${idPrefix}-line-hidden`}
        type="line"
        filter={['==', ['get', 'visible'], false]}
        paint={{
          'line-color': ZORDER_COLOR,
          'line-width': 2,
          'line-opacity': lineOpacity / 1.5,
          'line-dasharray': [2, 2],
        }}
      />
      {!muted && (
        <Layer
          id={`${idPrefix}-highlight`}
          type="line"
          filter={['in', ['get', 'id'], ['literal', highlightIds]]}
          paint={{ 'line-color': ZORDER_COLOR, 'line-width': 4 }}
        />
      )}
    </Source>
  )
}
